/// <reference types="vite/client" />
// src/client/renderer/shader-registry.ts
import type { ShaderFactory, ShaderId, ShaderRegistry } from './shaders/types';
import { createSkyShader } from './shaders/sky';
import { createWindSwayShader } from './shaders/wind-sway';
import { createCloudNoiseShader } from './shaders/cloud-noise';
import { createWaterShader } from './shaders/water';
import { createWaterfallShader } from './shaders/waterfall';
import { createDissolveShader } from './shaders/dissolve';
import { createFresnelRimShader } from './shaders/fresnel-rim';
import { createOutlineShader } from './shaders/outline';
import { createHitFlashShader } from './shaders/hit-flash';
import { createCapeShader } from './shaders/cape-shader';
import { createComicBubbleShader } from './shaders/comic-bubble';
import { createStoneSkinShader } from './shaders/stone-skin';

export function createShaderRegistry(): ShaderRegistry {
  const factories = new Map<ShaderId, ShaderFactory>();
  return {
    get size() {
      return factories.size;
    },
    get ids() {
      return Array.from(factories.keys()) as readonly ShaderId[];
    },
    has(id) {
      return factories.has(id);
    },
    get(id) {
      const f = factories.get(id);
      if (!f) throw new Error(`ShaderRegistry: unknown shader id "${id}"`);
      return f;
    },
    register(id, factory) {
      factories.set(id, factory);
    },
  };
}

/** Convenience: build a registry with all 12 shaders pre-registered
 *  (7 from Plan 11 + 'outline' and 'hit-flash' added in Plan 14a Task 4.1
 *  + 'cape' added in Plan 13 Task 3.1 + 'comic-bubble' added in Plan 14b Task 2.1
 *  + 'stone-skin' for rig-attached Stone Skin status shells).
 *  Insertion order is deterministic so `.ids` is stable across runs. */
export function createDefaultShaderRegistry(): ShaderRegistry {
  const r = createShaderRegistry();
  r.register('sky', createSkyShader);
  r.register('wind-sway', createWindSwayShader);
  r.register('cloud-noise', createCloudNoiseShader);
  r.register('water', createWaterShader);
  r.register('waterfall', createWaterfallShader);
  r.register('dissolve', createDissolveShader);
  r.register('fresnel-rim', createFresnelRimShader);
  r.register('outline', createOutlineShader);
  r.register('hit-flash', createHitFlashShader);
  r.register('cape', createCapeShader);
  r.register('comic-bubble', createComicBubbleShader);
  r.register('stone-skin', createStoneSkinShader);
  return r;
}

// HMR plumbing is dev-only and gated by import.meta.env.DEV && import.meta.hot. Vitest runs without these globals so the block is unreachable in tests; production bundles tree-shake it. Accept-list MUST include every shader source file that createDefaultShaderRegistry registers — when adding a shader, update this accept list in lockstep with the registration.
if (import.meta.env.DEV && import.meta.hot) {
  import.meta.hot.accept(
    [
      './shaders/outline',
      './shaders/hit-flash',
      './shaders/cape',
      './shaders/comic-bubble',
      './shaders/stone-skin',
    ],
    (mods) => {
      // Re-register updated factories on the singleton registry.
      // Production bundles: this block is dead-code-eliminated.
      void mods;
    },
  );
}
